import { useDateLocale } from "@/hooks/useDateLocale";
import { useState, useRef, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { ListSkeleton } from "@/components/PageSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetExpenses, apiCreateExpense, apiSubmitExpense, apiDeleteExpense,
  apiUploadExpenseReceipt, apiDeleteExpenseReceipt, apiGetExpense,
  getExpenseReceiptUrl, apiGetCompanyCurrency, apiRefreshFileToken,
  apiGetCashAdvances, apiCreateCashAdvance, apiSubmitCashAdvanceReport,
  apiUploadCashAdvanceReceipt, apiDeleteCashAdvanceReceipt,
  apiGetCashAdvance, getCashAdvanceReceiptUrl,
  apiCancelCashAdvance,
  apiApproveCashAdvanceReport, apiRejectCashAdvanceReport,
  type Expense, type CashAdvance, type CashAdvanceReceipt,
} from "@/lib/api";
import {
  ReceiptText, Plus, Send, Trash2, Upload, FileText, X,
  Clock, CheckCircle2, XCircle, Banknote, RefreshCw, AlertCircle,
  Wallet, ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell,
} from "recharts";

// ─── Status badge ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType; color: string }> = {
  draft:            { label: "Draft",            variant: "secondary",   icon: FileText,    color: "#94a3b8" },
  submitted:        { label: "Submitted",         variant: "outline",     icon: Clock,       color: "#60a5fa" },
  pending_approval: { label: "Pending Approval",  variant: "outline",     icon: Clock,       color: "#f59e0b" },
  approved:         { label: "Approved",          variant: "default",     icon: CheckCircle2, color: "#22c55e" },
  rejected:         { label: "Rejected",          variant: "destructive", icon: XCircle,     color: "#ef4444" },
  reimbursed:       { label: "Reimbursed",        variant: "default",     icon: Banknote,    color: "#8b5cf6" },
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[status] ?? { label: status, variant: "outline" as const, icon: AlertCircle, color: "#94a3b8" };
  const Icon = cfg.icon;
  return (
    <Badge variant={cfg.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {t(`expenses.status.${status}`, cfg.label)}
    </Badge>
  );
}

// ─── Categories ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: "travel",        label: "Travel" },
  { value: "food",          label: "Food & Meals" },
  { value: "office",        label: "Office Supplies" },
  { value: "accommodation", label: "Accommodation" },
  { value: "software",      label: "Software & Subscriptions" },
  { value: "other",         label: "Other" },
];

const CATEGORY_COLORS: Record<string, string> = {
  travel: "#3b82f6",
  food: "#f59e0b",
  office: "#10b981",
  accommodation: "#8b5cf6",
  software: "#06b6d4",
  other: "#94a3b8",
};

const PAID_BY_OPTIONS = [
  { value: "employee",     labelKey: "employee" },
  { value: "company_card", labelKey: "company_card" },
  { value: "other",        labelKey: "other" },
];

// ════════════════════════════════════════════════════════════════════
// Main page
// ════════════════════════════════════════════════════════════════════

export default function MyExpenses() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Keep a short-lived file-access token fresh for loading private receipt files.
  useEffect(() => {
    apiRefreshFileToken();
    const id = setInterval(apiRefreshFileToken, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const [tab, setTab] = useState("list");
  const [submitExpenseOpen, setSubmitExpenseOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Cash advance state
  const [requestAdvanceOpen, setRequestAdvanceOpen] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState<CashAdvance | null>(null);
  const [advanceDetailOpen, setAdvanceDetailOpen] = useState(false);
  const [submitReportOpen, setSubmitReportOpen] = useState(false);
  const [submitReportAdvance, setSubmitReportAdvance] = useState<CashAdvance | null>(null);
  const [caRejectDialog, setCaRejectDialog] = useState<CashAdvance | null>(null);
  const [caRejectType, setCaRejectType] = useState<"report_lm">("report_lm");
  const [caRejectReason, setCaRejectReason] = useState("");

  // Detect if user is a line manager
  const isLM = user?.role?.subRole === "line_manager" ||
    (user?.role?.mainRole === "user" && (user as any)?.isLineManager);

  // ── Always scope to the current user's own employeeId.
  // For regular employees the backend already scopes; for HR/admin passing
  // employeeId applies the WHERE filter and prevents seeing all expenses.
  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ["my-expenses", user?.employeeId],
    queryFn: () =>
      apiGetExpenses(user?.employeeId ? { employeeId: Number(user.employeeId) } : undefined),
  });

  const { data: currencyData } = useQuery({
    queryKey: ["company-currency"],
    queryFn: apiGetCompanyCurrency,
  });
  const currency = currencyData?.currency ?? "MKD";

  const submitMutation = useMutation({
    mutationFn: (id: number) => apiSubmitExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-expenses"] });
      toast({ title: "Expense submitted for approval" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-expenses"] });
      toast({ title: "Expense deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const openDetail = async (expense: Expense) => {
    try {
      const full = await apiGetExpense(expense.id);
      setSelectedExpense(full);
      setDetailOpen(true);
    } catch {
      setSelectedExpense(expense);
      setDetailOpen(true);
    }
  };

  // Cash advances query — don't pass employeeId; backend scopes:
  // employees see own, LMs see direct reports', HR/admin see all
  const { data: allCashAdvances = [], isLoading: caLoading } = useQuery({
    queryKey: ["my-cash-advances"],
    queryFn: () => apiGetCashAdvances(),
  });
  // Only show own cash advances in My Expenses (team advances are handled via My Tasks workflow)
  const myCashAdvances = allCashAdvances.filter((a) => a.employeeId === Number(user?.employeeId));

  const caApproveReportMutation = useMutation({
    mutationFn: (id: number) => apiApproveCashAdvanceReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-cash-advances"] });
      toast({ title: "Expense report approved" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const caRejectReportMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => apiRejectCashAdvanceReport(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-cash-advances"] });
      setCaRejectDialog(null);
      setCaRejectReason("");
      toast({ title: "Expense report rejected" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const caCancelMutation = useMutation({
    mutationFn: (id: number) => apiCancelCashAdvance(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-cash-advances"] });
      toast({ title: "Cash advance cancelled" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const openAdvanceDetail = async (adv: CashAdvance) => {
    try {
      const full = await apiGetCashAdvance(adv.id);
      setSelectedAdvance(full);
    } catch {
      setSelectedAdvance(adv);
    }
    setAdvanceDetailOpen(true);
  };

  const openSubmitReport = (adv: CashAdvance) => {
    setSubmitReportAdvance(adv);
    setSubmitReportOpen(true);
  };

  // ── Chart data ─────────────────────────────────────────────────────────────

  const monthlyData = useMemo(() => {
    const map: Record<string, number> = {};
    expenses.forEach((e) => {
      const key = e.expenseDate.slice(0, 7); // "2025-03"
      map[key] = (map[key] ?? 0) + e.amount;
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, total]) => ({
        month: month.slice(5), // show "03" → format as "Mar 25" below
        fullMonth: month,
        total: Math.round(total * 100) / 100,
      }));
  }, [expenses]);

  const categoryData = useMemo(() => {
    const map: Record<string, { total: number; key: string }> = {};
    expenses.forEach((e) => {
      const label = t(`expenses.categories.${e.category}`, CATEGORIES.find((c) => c.value === e.category)?.label ?? e.category);
      if (!map[label]) map[label] = { total: 0, key: e.category };
      map[label].total += e.amount;
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([name, { total, key }]) => ({
        name,
        total: Math.round(total * 100) / 100,
        color: CATEGORY_COLORS[key] ?? "#94a3b8",
      }));
  }, [expenses, t]);

  const statusSummary = useMemo(() => {
    const map: Record<string, { count: number; total: number }> = {};
    expenses.forEach((e) => {
      if (!map[e.status]) map[e.status] = { count: 0, total: 0 };
      map[e.status].count++;
      map[e.status].total += e.amount;
    });
    return map;
  }, [expenses]);

  const totalSpend = useMemo(
    () => expenses.reduce((s, e) => s + e.amount, 0),
    [expenses],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <ReceiptText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">
                {t("expenses.myTitle", "My Expenses")}
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {t("expenses.mySubtitle", "Submit and track your expense claims")}
              </p>
            </div>
          </div>
          {tab === "advances" ? (
            <Button onClick={() => setRequestAdvanceOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t("expenses.requestAdvance", "Request Cash Advance")}
            </Button>
          ) : (
            <Button onClick={() => setSubmitExpenseOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t("expenses.submit", "Submit Expense")}
            </Button>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="list" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <ReceiptText className="h-3.5 w-3.5" />
              {t("expenses.tabs.myExpenses", "My Expenses")}
            </TabsTrigger>
            <TabsTrigger value="advances" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <Wallet className="h-3.5 w-3.5" />
              {t("expenses.tabs.cashAdvances", "Cash Advances")}
              {(() => {
                const actionCount =
                  myCashAdvances.filter((a) => ["approved", "report_rejected"].includes(a.status)).length;
                return actionCount > 0 ? (
                  <Badge variant="destructive" className="ml-1 h-4 px-1 text-[10px]">{actionCount}</Badge>
                ) : null;
              })()}
            </TabsTrigger>
          </TabsList>

          {/* ── List tab ── */}
          <TabsContent value="list" className="space-y-3 mt-4">
            {isLoading ? (
              <ListSkeleton rows={4} />
            ) : expenses.length === 0 ? (
              <Card>
                <CardContent className="py-12 flex flex-col items-center gap-3 text-muted-foreground">
                  <ReceiptText className="h-10 w-10 opacity-30" />
                  <p>{t("expenses.noExpenses", "No expenses yet. Submit your first expense to get started.")}</p>
                  <Button variant="outline" size="sm" onClick={() => setSubmitExpenseOpen(true)}>
                    <Plus className="h-4 w-4 mr-1.5" />{t("expenses.submit", "Submit Expense")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Expense cards */}
                {expenses.map((exp) => (
                  <ExpenseCard
                    key={exp.id}
                    expense={exp}
                    onView={() => openDetail(exp)}
                    onSubmit={() => submitMutation.mutate(exp.id)}
                    onDelete={() => deleteMutation.mutate(exp.id)}
                  />
                ))}

                {/* ── Visual summary ── */}
                <div className="space-y-4 pt-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    {t("expenses.myOverview", "My Spending Overview")}
                  </h3>

                  {/* Status summary pills */}
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusSummary).map(([status, { count, total }]) => {
                      const cfg = STATUS_CONFIG[status] ?? { label: status, color: "#94a3b8", icon: AlertCircle, variant: "outline" as const };
                      return (
                        <div
                          key={status}
                          className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm bg-card"
                        >
                          <span
                            className="h-2 w-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: cfg.color }}
                          />
                          <span className="font-medium">{t(`expenses.status.${status}`, cfg.label)}</span>
                          <span className="text-muted-foreground">
                            {count} · {total.toLocaleString()} {currency}
                          </span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm bg-muted/40 font-semibold">
                      {t("expenses.total", "Total")}: {totalSpend.toLocaleString()} {currency}
                    </div>
                  </div>

                  {/* Charts row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Monthly spend */}
                    {monthlyData.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t("expenses.charts.monthlySpend", "Monthly Spend")}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={monthlyData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                              <XAxis
                                dataKey="fullMonth"
                                tickFormatter={(v: string) => {
                                  const [y, m] = v.split("-");
                                  return new Date(Number(y), Number(m) - 1).toLocaleString("default", { month: "short" }) + " " + y.slice(2);
                                }}
                                tick={{ fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{ fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                width={50}
                                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                              />
                              <Tooltip
                                formatter={(value: number) => [`${value.toLocaleString()} ${currency}`, "Total"]}
                                labelFormatter={(label: string) => {
                                  const [y, m] = label.split("-");
                                  return new Date(Number(y), Number(m) - 1).toLocaleString("default", { month: "long", year: "numeric" });
                                }}
                              />
                              <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}

                    {/* By category */}
                    {categoryData.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t("expenses.charts.byCategory", "By Category")}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart
                              data={categoryData}
                              layout="vertical"
                              margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                            >
                              <XAxis
                                type="number"
                                tick={{ fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                              />
                              <YAxis
                                type="category"
                                dataKey="name"
                                tick={{ fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                width={110}
                              />
                              <Tooltip
                                formatter={(value: number) => [`${value.toLocaleString()} ${currency}`, "Total"]}
                              />
                              <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                                {categoryData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* ── Cash Advances tab ── */}
          <TabsContent value="advances" className="space-y-6 mt-4">
            {/* ── My cash advances ── */}
            <div className="space-y-3">

              {caLoading ? (
                <ListSkeleton rows={3} />
              ) : myCashAdvances.length === 0 ? (
                <Card>
                  <CardContent className="py-12 flex flex-col items-center gap-3 text-muted-foreground">
                    <Wallet className="h-10 w-10 opacity-30" />
                    <p>{t("expenses.noAdvances", "No cash advance requests yet.")}</p>
                    <Button variant="outline" size="sm" onClick={() => setRequestAdvanceOpen(true)}>
                      <Plus className="h-4 w-4 mr-1.5" />{t("expenses.requestAdvance", "Request Cash Advance")}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {myCashAdvances.map((adv) => (
                    <CashAdvanceCard
                      key={adv.id}
                      advance={adv}
                      currency={currency}
                      onView={() => openAdvanceDetail(adv)}
                      onSubmitReport={() => openSubmitReport(adv)}
                      onCancel={() => caCancelMutation.mutate(adv.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Submit Expense dialog */}
        <Dialog open={submitExpenseOpen} onOpenChange={setSubmitExpenseOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ReceiptText className="h-5 w-5" />
                {t("expenses.submitNew", "Submit New Expense")}
              </DialogTitle>
              <DialogDescription>
                {t("expenses.submitNewDesc", "Fill in the details below to submit a new expense claim for approval.")}
              </DialogDescription>
            </DialogHeader>
            <SubmitExpenseForm
              currency={currency}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ["my-expenses"] });
                setSubmitExpenseOpen(false);
                toast({ title: "Expense submitted for approval" });
              }}
              onCancel={() => setSubmitExpenseOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Detail dialog */}
        {selectedExpense && (
          <ExpenseDetailDialog
            expense={selectedExpense}
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            onSubmit={() => {
              submitMutation.mutate(selectedExpense.id);
              setDetailOpen(false);
            }}
          />
        )}

        {/* Request Cash Advance dialog */}
        <RequestCashAdvanceDialog
          open={requestAdvanceOpen}
          currency={currency}
          onClose={() => setRequestAdvanceOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["my-cash-advances"] });
            setRequestAdvanceOpen(false);
            toast({ title: "Cash advance request submitted" });
          }}
        />

        {/* Cash Advance detail dialog */}
        {selectedAdvance && (
          <CashAdvanceDetailDialog
            advance={selectedAdvance}
            open={advanceDetailOpen}
            onClose={() => { setAdvanceDetailOpen(false); setSelectedAdvance(null); }}
            onSubmitReport={() => {
              setAdvanceDetailOpen(false);
              openSubmitReport(selectedAdvance);
            }}
          />
        )}

        {/* Submit Report dialog */}
        {submitReportAdvance && (
          <SubmitReportDialog
            advance={submitReportAdvance}
            open={submitReportOpen}
            onClose={() => { setSubmitReportOpen(false); setSubmitReportAdvance(null); }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["my-cash-advances"] });
              setSubmitReportOpen(false);
              setSubmitReportAdvance(null);
              toast({ title: t("expenses.toast.reportSubmitted", "Expense report submitted successfully") });
            }}
          />
        )}

        {/* LM Reject dialog */}
        <Dialog open={!!caRejectDialog} onOpenChange={(v) => !v && setCaRejectDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{caRejectType === "report_lm" ? t("expenses.modals.rejectReportTitle", "Reject Expense Report") : t("expenses.modals.rejectCaTitle", "Reject Cash Advance")}</DialogTitle>
              <DialogDescription>
                {caRejectDialog && `${caRejectDialog.employeeName} — ${caRejectDialog.amount.toLocaleString()} ${caRejectDialog.currency}`}
              </DialogDescription>
            </DialogHeader>
            <FloatField
              label={t("expenses.modals.rejectionReasonLabel", "Rejection Reason")}
              required
              type="textarea"
              value={caRejectReason}
              onChange={(v) => setCaRejectReason(v as string)}
              placeholder={t("expenses.modals.rejectionReasonPlaceholder", "Explain why this is being rejected...")}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCaRejectDialog(null)}>{t("common.cancel", "Cancel")}</Button>
              <Button
                variant="destructive"
                disabled={!caRejectReason.trim() || caRejectReportMutation.isPending}
                onClick={() => {
                  if (!caRejectDialog) return;
                  caRejectReportMutation.mutate({ id: caRejectDialog.id, reason: caRejectReason });
                }}
              >
                {t("expenses.modals.confirmRejection", "Confirm Rejection")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

// ─── Expense card ─────────────────────────────────────────────────────────────

function ExpenseCard({
  expense,
  onView,
  onSubmit,
  onDelete,
}: {
  expense: Expense;
  onView: () => void;
  onSubmit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={onView}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{expense.title}</span>
            <StatusBadge status={expense.status} />
          </div>
          <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap gap-3">
            <span>{t(`expenses.categories.${expense.category}`, CATEGORIES.find((c) => c.value === expense.category)?.label ?? expense.category)}</span>
            <span>{format(new Date(expense.expenseDate), "dd MMM yyyy", { locale: dateLocale })}</span>
            {expense.submittedAt && (
              <span>{t("expenses.status.submitted")} {format(new Date(expense.submittedAt), "dd MMM yyyy", { locale: dateLocale })}</span>
            )}
          </div>
          {expense.rejectionReason && (
            <p className="text-xs text-destructive mt-1 flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              {expense.rejectionReason}
            </p>
          )}
        </div>

        <div className="text-right shrink-0">
          <p className="font-semibold">
            {expense.amount.toLocaleString()} {expense.currency}
          </p>
        </div>

        {/* Action buttons — stopPropagation prevents triggering the card click */}
        <div className="flex items-center gap-1">
          {expense.status === "draft" && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); onSubmit(); }}
                title="Submit for approval"
              >
                <Send className="h-4 w-4 text-primary" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                title={t("pilot.task.delete")}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          )}
          {expense.status === "rejected" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); onSubmit(); }}
              title="Resubmit"
            >
              <RefreshCw className="h-4 w-4 text-primary" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Submit form ──────────────────────────────────────────────────────────────

function SubmitExpenseForm({ currency, onSuccess, onCancel }: { currency: string; onSuccess: () => void; onCancel?: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    title: "",
    amount: "",
    expenseDate: new Date().toISOString().slice(0, 10),
    category: "other",
    paidBy: "employee",
    description: "",
    taxAmount: "",
    taxType: "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async () => {
    if (!form.title || !form.amount || !form.expenseDate || !form.category) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // Create draft
      const expense = await apiCreateExpense({
        title: form.title,
        amount: Number(form.amount),
        currency,
        expenseDate: form.expenseDate,
        category: form.category,
        paidBy: form.paidBy,
        description: form.description || undefined,
        taxAmount: form.taxAmount ? Number(form.taxAmount) : undefined,
        taxType: form.taxType || undefined,
      });

      // Upload receipts
      for (const file of files) {
        try { await apiUploadExpenseReceipt(expense.id, file); } catch {}
      }

      // Submit
      await apiSubmitExpense(expense.id);
      onSuccess();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
  };

  return (
    <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
          {/* Title */}
          <div className="md:col-span-2">
            <FloatField
              label={t("expenses.expenseName")}
              required
              value={form.title}
              onChange={(v) => set("title", v as string)}
              placeholder={t("expenses.titlePlaceholder", "e.g. Client lunch, Flight to Berlin")}
            />
          </div>

          {/* Amount */}
          <div>
            <FloatWrapper label={t("expenses.amount", "Amount")} required>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => set("amount", e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 pr-16 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                  {currency}
                </span>
              </div>
            </FloatWrapper>
          </div>

          {/* Date */}
          <div>
            <FloatField
              label={t("expenses.expenseDate", "Expense Date")}
              required
              type="date"
              value={form.expenseDate}
              onChange={(v) => set("expenseDate", v as string)}
            />
          </div>

          {/* Category */}
          <div>
            <FloatWrapper label={t("expenses.categoryLabel", "Category")} required>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{t(`expenses.categories.${c.value}`, c.label)}</SelectItem>)}
                </SelectContent>
              </Select>
            </FloatWrapper>
          </div>

          {/* Paid by */}
          <div>
            <FloatWrapper label={t("expenses.paidByLabel", "Paid By")}>
              <Select value={form.paidBy} onValueChange={(v) => set("paidBy", v)}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAID_BY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{t(`expenses.paidBy.${o.labelKey}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </FloatWrapper>
          </div>

          {/* Tax amount */}
          <div>
            <FloatWrapper label={t("expenses.taxAmount", "Tax Amount (optional)")}>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.taxAmount}
                  onChange={(e) => set("taxAmount", e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 pr-16 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                  {currency}
                </span>
              </div>
            </FloatWrapper>
          </div>

          {/* Tax type */}
          <div>
            <FloatWrapper label={t("expenses.taxType", "Tax Type (optional)")}>
              <Select value={form.taxType || "_none"} onValueChange={(v) => set("taxType", v === "_none" ? "" : v)}>
                <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                  <SelectValue placeholder={t("common.select", "Select...")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t("common.none", "None")}</SelectItem>
                  <SelectItem value="VAT">VAT</SelectItem>
                  <SelectItem value="other">{t("expenses.categories.other", "Other")}</SelectItem>
                </SelectContent>
              </Select>
            </FloatWrapper>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <FloatField
              label={t("expenses.description", "Description (optional)")}
              type="textarea"
              value={form.description}
              onChange={(v) => set("description", v as string)}
              placeholder={t("expenses.descriptionPlaceholder", "Additional notes about this expense...")}
            />
          </div>

          {/* Receipt upload */}
          <div className="md:col-span-2">
            <Label>{t("expenses.receipts", "Receipts (optional)")}</Label>
            <div
              className="mt-1 border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-accent/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("expenses.uploadHint", "Click to upload or drag & drop · PDF, JPG, PNG up to 20MB")}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileChange}
              />
            </div>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>{t("common.cancel", "Cancel")}</Button>
          )}
          <Button onClick={handleSubmit} disabled={submitting} className="gap-2">
            <Send className="h-4 w-4" />
            {submitting ? t("expenses.submitting", "Submitting…") : t("expenses.submitRequest", "Submit for Approval")}
          </Button>
        </DialogFooter>
    </div>
  );
}

// ─── Cash Advance status config ──────────────────────────────────────────────

const CA_STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  pending_lm:        { label: "Awaiting Manager",     color: "#f59e0b", icon: Clock },
  pending_hr:        { label: "Awaiting HR",           color: "#f59e0b", icon: Clock },
  approved:          { label: "Approved — Submit Report", color: "#22c55e", icon: CheckCircle2 },
  report_submitted:  { label: "Report Under Review",  color: "#3b82f6", icon: Clock },
  report_pending_hr: { label: "Awaiting HR Settlement", color: "#3b82f6", icon: Clock },
  report_rejected:   { label: "Report Rejected",      color: "#ef4444", icon: XCircle },
  settled:           { label: "Settled",              color: "#8b5cf6", icon: Banknote },
  rejected:          { label: "Rejected",             color: "#ef4444", icon: XCircle },
  cancelled:         { label: "Cancelled",            color: "#94a3b8", icon: XCircle },
};

function CashAdvanceStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cfg = CA_STATUS_CONFIG[status] ?? { label: status, color: "#94a3b8", icon: AlertCircle };
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border"
      style={{ color: cfg.color, borderColor: cfg.color + "40", backgroundColor: cfg.color + "18" }}
    >
      <Icon className="h-3 w-3" />
      {t(`expenses.status.${status}`, cfg.label)}
    </span>
  );
}

// ─── Cash Advance card ────────────────────────────────────────────────────────

function CashAdvanceCard({
  advance,
  currency,
  onView,
  onSubmitReport,
  onCancel,
}: {
  advance: CashAdvance;
  currency: string;
  onView: () => void;
  onSubmitReport: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const needsReport = advance.status === "approved" || advance.status === "report_rejected";
  const canCancel = advance.status === "pending_lm";

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={onView}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{advance.purpose}</span>
            <CashAdvanceStatusBadge status={advance.status} />
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            Requested {format(new Date(advance.createdAt), "dd MMM yyyy")}
            {advance.settledAt && ` · Settled ${format(new Date(advance.settledAt), "dd MMM yyyy")}`}
          </div>
          {advance.rejectionReason && (
            <p className="text-xs text-destructive mt-1 flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              {advance.rejectionReason}
            </p>
          )}
          {advance.status === "settled" && advance.settlementType && advance.settlementType !== "none" && (
            <p className="text-xs mt-1" style={{ color: advance.settlementType === "payout" ? "#22c55e" : "#f59e0b" }}>
              {advance.settlementType === "payout"
                ? `Payout: ${advance.settlementAmount?.toLocaleString()} ${advance.currency}`
                : `Deduction: ${advance.settlementAmount?.toLocaleString()} ${advance.currency}`}
            </p>
          )}
        </div>

        <div className="text-right shrink-0">
          <p className="font-semibold">{advance.amount.toLocaleString()} {advance.currency}</p>
          {advance.reportAmount !== null && (
            <p className="text-xs text-muted-foreground">
              Report: {advance.reportAmount.toLocaleString()} {advance.currency}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1">
          {needsReport && (
            <Button
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={(e) => { e.stopPropagation(); onSubmitReport(); }}
            >
              <Send className="h-3.5 w-3.5" />
              {advance.status === "report_rejected" ? t("expenses.resubmit", "Resubmit") : t("expenses.submitReport", "Submit Report")}
            </Button>
          )}
          {canCancel && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1 text-muted-foreground hover:text-destructive shrink-0"
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
            >
              <XCircle className="h-3.5 w-3.5" />
              {t("common.cancel", "Cancel")}
            </Button>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Request Cash Advance dialog ──────────────────────────────────────────────

function RequestCashAdvanceDialog({
  open,
  currency,
  onClose,
  onSuccess,
}: {
  open: boolean;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState({ amount: "", currency: currency, purpose: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async () => {
    if (!form.amount || !form.purpose.trim()) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await apiCreateCashAdvance({
        amount: Number(form.amount),
        currency: form.currency,
        purpose: form.purpose.trim(),
      });
      onSuccess();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            {t("expenses.advance.requestTitle", "Request Cash Advance")}
          </DialogTitle>
          <DialogDescription>
            {t("expenses.advance.requestDesc", "Submit a cash advance request. It will be reviewed by your manager and HR.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FloatWrapper label={t("expenses.amount", "Amount")} required>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 pr-16 text-sm outline-none focus:ring-2 focus:ring-primary/20"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                {form.currency}
              </span>
            </div>
          </FloatWrapper>
          <FloatField
            label={t("expenses.purpose", "Purpose")}
            required
            type="textarea"
            value={form.purpose}
            onChange={(v) => set("purpose", v as string)}
            placeholder="Describe the business purpose of this cash advance…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="gap-2">
            <Send className="h-4 w-4" />
            {submitting ? t("expenses.submitting", "Submitting…") : t("expenses.submitRequest", "Submit Request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Submit Report dialog ─────────────────────────────────────────────────────

function SubmitReportDialog({
  advance,
  open,
  onClose,
  onSuccess,
}: {
  advance: CashAdvance;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reportAmount, setReportAmount] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedReceipts, setUploadedReceipts] = useState<CashAdvanceReceipt[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const diff = reportAmount ? Number(reportAmount) - advance.amount : null;
  const diffLabel =
    diff === null
      ? null
      : diff === 0
      ? t("expenses.advance.diffMatch", "Exact match")
      : diff > 0
      ? t("expenses.advance.diffOver", { diff: diff.toFixed(2), currency: advance.currency, defaultValue: `Over by ${diff.toFixed(2)} ${advance.currency} — HR will pay out the difference` })
      : t("expenses.advance.diffUnder", { diff: Math.abs(diff).toFixed(2), currency: advance.currency, defaultValue: `Under by ${Math.abs(diff).toFixed(2)} ${advance.currency} — difference will be deducted from salary` });
  const diffColor = diff === null ? undefined : diff === 0 ? "#22c55e" : diff > 0 ? "#3b82f6" : "#f59e0b";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
  };

  const handleSubmit = async () => {
    if (!reportAmount) {
      toast({ title: "Please enter the report amount", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      // Upload receipts first
      const uploaded: CashAdvanceReceipt[] = [...uploadedReceipts];
      for (const file of files) {
        try {
          const att = await apiUploadCashAdvanceReceipt(advance.id, file);
          uploaded.push(att);
        } catch {}
      }
      setUploadedReceipts(uploaded);
      setFiles([]);

      // Submit report
      await apiSubmitCashAdvanceReport(advance.id, {
        reportAmount: Number(reportAmount),
        reportNotes: reportNotes.trim() || undefined,
      });
      onSuccess();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5" />
            {t("expenses.advance.reportTitle", "Submit Expense Report")}
          </DialogTitle>
          <DialogDescription>
            {t("expenses.advance.reportDesc", { id: advance.id, amount: advance.amount.toLocaleString(), currency: advance.currency, defaultValue: `Submit your expense report for cash advance #${advance.id} (${advance.amount.toLocaleString()} ${advance.currency}).` })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Advance summary */}
          <div className="bg-muted/40 rounded-lg p-3 text-sm">
            <p className="font-medium mb-1">{t("expenses.advance.detailsTitle", "Cash Advance Details")}</p>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground">
              <span>{t("expenses.amount", "Amount")}</span>
              <span className="font-semibold text-foreground">{advance.amount.toLocaleString()} {advance.currency}</span>
              <span>{t("expenses.purpose", "Purpose")}</span>
              <span>{advance.purpose}</span>
            </div>
          </div>

          <div>
            <FloatWrapper label={t("expenses.advance.actualSpent", "Actual Amount Spent")} required>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={reportAmount}
                  onChange={(e) => setReportAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4 pr-16 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                  {advance.currency}
                </span>
              </div>
            </FloatWrapper>
            {diffLabel && (
              <p className="text-xs mt-1.5 font-medium" style={{ color: diffColor }}>{diffLabel}</p>
            )}
          </div>

          <FloatField
            label={t("expenses.advance.notes", "Notes (optional)")}
            type="textarea"
            value={reportNotes}
            onChange={(v) => setReportNotes(v as string)}
            placeholder="Additional notes about your expenses…"
          />

          {/* Receipt upload */}
          <div>
            <Label>{t("expenses.receiptsReq", "Receipts & Proof")}</Label>
            <div
              className="mt-1 border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:bg-accent/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("expenses.uploadHint", "Click to upload receipts · PDF, JPG, PNG up to 20MB")}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileChange}
              />
            </div>
            {/* Already-uploaded receipts */}
            {uploadedReceipts.map((att) => (
              <div key={att.id} className="flex items-center gap-2 text-sm mt-1">
                <FileText className="h-4 w-4 text-green-500" />
                <a
                  href={getCashAdvanceReceiptUrl(advance.id, att.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline flex-1 truncate"
                >
                  {att.fileName}
                </a>
                <button
                  type="button"
                  onClick={async () => {
                    await apiDeleteCashAdvanceReceipt(advance.id, att.id);
                    setUploadedReceipts((prev) => prev.filter((r) => r.id !== att.id));
                  }}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {/* Pending-upload files */}
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm mt-1">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="gap-2">
            <Send className="h-4 w-4" />
            {submitting ? t("expenses.submitting", "Submitting…") : t("expenses.submitReport", "Submit Report")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cash Advance detail dialog ───────────────────────────────────────────────

function CashAdvanceDetailDialog({
  advance,
  open,
  onClose,
  onSubmitReport,
}: {
  advance: CashAdvance;
  open: boolean;
  onClose: () => void;
  onSubmitReport: () => void;
}) {
  const { t } = useTranslation();
  const needsReport = advance.status === "approved" || advance.status === "report_rejected";
  const diff = advance.reportAmount !== null ? advance.reportAmount - advance.amount : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Cash Advance #{advance.id}
          </DialogTitle>
          <DialogDescription>
            <CashAdvanceStatusBadge status={advance.status} />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div className="text-muted-foreground">{t("expenses.details.amount", "Amount")}</div>
            <div className="font-semibold">{advance.amount.toLocaleString()} {advance.currency}</div>
            <div className="text-muted-foreground">{t("expenses.purpose", "Purpose")}</div>
            <div>{advance.purpose}</div>
            <div className="text-muted-foreground">{t("expenses.details.requested", "Requested")}</div>
            <div>{format(new Date(advance.createdAt), "dd MMM yyyy")}</div>
            {advance.reportAmount !== null && (
              <>
                <div className="text-muted-foreground">{t("expenses.details.reportAmount", "Report Amount")}</div>
                <div className="font-semibold">
                  {advance.reportAmount.toLocaleString()} {advance.currency}
                  {diff !== null && diff !== 0 && (
                    <span className="text-xs ml-1" style={{ color: diff > 0 ? "#3b82f6" : "#f59e0b" }}>
                      ({diff > 0 ? "+" : ""}{diff.toFixed(2)})
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Timeline */}
          <div>
            <p className="text-muted-foreground font-medium mb-2 text-xs uppercase tracking-wide">{t("expenses.details.timeline", "Timeline")}</p>
            <div className="space-y-1.5">
              <TimelineEntry done={true} label={t("expenses.timeline.requestSubmitted", "Request submitted")} date={advance.createdAt} />
              <TimelineEntry done={!!advance.lmApprovedAt} label={t("expenses.timeline.managerApproved", "Manager approved")} date={advance.lmApprovedAt} />
              <TimelineEntry done={!!advance.hrApprovedAt} label={t("expenses.timeline.hrApproved", "HR approved")} date={advance.hrApprovedAt} />
              <TimelineEntry done={!!advance.reportSubmittedAt} label={t("expenses.timeline.expenseReportSubmitted", "Expense report submitted")} date={advance.reportSubmittedAt} />
              <TimelineEntry done={!!advance.reportLmApprovedAt} label={t("expenses.timeline.managerApprovedReport", "Manager approved report")} date={advance.reportLmApprovedAt} />
              <TimelineEntry done={!!advance.settledAt} label={t("expenses.timeline.settledByHr", "Settled by HR")} date={advance.settledAt} />
            </div>
          </div>

          {/* Rejection reason */}
          {advance.rejectionReason && (
            <div className="bg-destructive/10 border border-destructive/20 rounded p-3">
              <p className="text-destructive text-xs font-medium mb-1 flex items-center gap-1">
                <XCircle className="h-3 w-3" />{t("expenses.details.rejectionReason", "Rejection Reason")}
              </p>
              <p>{advance.rejectionReason}</p>
            </div>
          )}

          {/* Settlement outcome */}
          {advance.status === "settled" && (
            <div className="bg-primary/5 border border-primary/20 rounded p-3">
              <p className="font-medium mb-1 flex items-center gap-1">
                <Banknote className="h-4 w-4" />{t("expenses.details.settlement", "Settlement")}
              </p>
              {advance.settlementType === "none" && <p>{t("expenses.advance.diffMatch", "Exact match")} — fully settled, no further action needed.</p>}
              {advance.settlementType === "payout" && (
                <p className="text-green-700 dark:text-green-400">
                  {t("expenses.advance.payout", { amount: advance.settlementAmount?.toLocaleString(), currency: advance.currency, defaultValue: `HR will pay out ${advance.settlementAmount?.toLocaleString()} ${advance.currency} (your expenses exceeded the advance).` })}
                </p>
              )}
              {advance.settlementType === "deduction" && (
                <p className="text-amber-700 dark:text-amber-400">
                  {t("expenses.advance.deduction", { amount: advance.settlementAmount?.toLocaleString(), currency: advance.currency, defaultValue: `${advance.settlementAmount?.toLocaleString()} ${advance.currency} will be deducted from your salary (your expenses were under the advance).` })}
                </p>
              )}
              {advance.settlementNotes && <p className="text-muted-foreground text-xs mt-1">{advance.settlementNotes}</p>}
            </div>
          )}

          {/* Receipts */}
          {advance.receipts && advance.receipts.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1">{t("expenses.details.receipts", "Receipts")}</p>
              <div className="space-y-1">
                {advance.receipts.map((att) => (
                  <div key={att.id} className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={getCashAdvanceReceiptUrl(advance.id, att.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline flex-1 truncate"
                    >
                      {att.fileName}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close", "Close")}</Button>
          {needsReport && (
            <Button onClick={onSubmitReport} className="gap-2">
              <Send className="h-4 w-4" />
              {advance.status === "report_rejected" ? t("expenses.resubmitReport", "Resubmit Report") : t("expenses.submitReport", "Submit Report")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TimelineEntry({ done, label, date }: { done: boolean; label: string; date?: string | null }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span
        className={`h-2 w-2 rounded-full flex-shrink-0 ${done ? "bg-green-500" : "bg-muted-foreground/30"}`}
      />
      <span className={done ? "text-foreground" : "text-muted-foreground"}>{label}</span>
      {done && date && (
        <span className="text-muted-foreground ml-auto">{format(new Date(date), "dd MMM yyyy")}</span>
      )}
    </div>
  );
}

// ─── Detail dialog ────────────────────────────────────────────────────────────

function ExpenseDetailDialog({
  expense,
  open,
  onClose,
  onSubmit,
}: {
  expense: Expense;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteAttachMutation = useMutation({
    mutationFn: ({ attachId }: { attachId: number }) => apiDeleteExpenseReceipt(expense.id, attachId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-expenses"] });
      toast({ title: "Attachment removed" });
    },
  });

  const isDraft = expense.status === "draft";
  const isRejected = expense.status === "rejected";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5" />
            {expense.title}
          </DialogTitle>
          <DialogDescription>
            <StatusBadge status={expense.status} />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div className="text-muted-foreground">{t("expenses.details.amount", "Amount")}</div>
            <div className="font-semibold">{expense.amount.toLocaleString()} {expense.currency}</div>
            <div className="text-muted-foreground">{t("expenses.details.category", "Category")}</div>
            <div>{t(`expenses.categories.${expense.category}`, CATEGORIES.find((c) => c.value === expense.category)?.label ?? expense.category)}</div>
            <div className="text-muted-foreground">{t("expenses.details.date", "Date")}</div>
            <div>{format(new Date(expense.expenseDate), "dd MMM yyyy", { locale: dateLocale })}</div>
            <div className="text-muted-foreground">{t("expenses.details.paidBy", "Paid By")}</div>
            <div>{(() => {
              const opt = PAID_BY_OPTIONS.find((o) => o.value === expense.paidBy);
              return opt ? t(`expenses.paidBy.${opt.labelKey}`) : expense.paidBy;
            })()}</div>
            {expense.taxAmount > 0 && (
              <>
                <div className="text-muted-foreground">{t("expenses.details.tax", "Tax")}</div>
                <div>{expense.taxAmount.toLocaleString()} {expense.currency}{expense.taxType ? ` (${expense.taxType})` : ""}</div>
              </>
            )}
          </div>

          {expense.description && (
            <div>
              <p className="text-muted-foreground mb-1">{t("expenses.details.description", "Description")}</p>
              <p className="bg-muted/50 rounded p-2">{expense.description}</p>
            </div>
          )}

          {expense.rejectionReason && (
            <div className="bg-destructive/10 border border-destructive/20 rounded p-3">
              <p className="text-destructive text-xs font-medium mb-1 flex items-center gap-1">
                <XCircle className="h-3 w-3" />{t("expenses.details.rejectionReason", "Rejection Reason")}
              </p>
              <p>{expense.rejectionReason}</p>
            </div>
          )}

          {/* Attachments */}
          {expense.attachments && expense.attachments.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1">{t("expenses.details.receipts", "Receipts")}</p>
              <div className="space-y-1">
                {expense.attachments.map((att) => (
                  <div key={att.id} className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={getExpenseReceiptUrl(expense.id, att.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline text-sm flex-1 truncate"
                    >
                      {att.fileName}
                    </a>
                    {isDraft && (
                      <button
                        type="button"
                        onClick={() => deleteAttachMutation.mutate({ attachId: att.id })}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close", "Close")}</Button>
          {(isDraft || isRejected) && (
            <Button onClick={onSubmit} className="gap-2">
              <Send className="h-4 w-4" />
              {isRejected ? t("expenses.resubmit", "Resubmit") : t("expenses.submitRequest", "Submit for Approval")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
